import type { FC } from 'react';
import styles from './ErrorDisplay.module.scss';
import { Alert } from '@mds/web-ui-alert';
import { IconAlertFilled } from '@mds/web-ui-icons';
import { Text } from '@mds/web-ui-text';
import type {ClpError} from '../../types/clpTypes';

interface ErrorDisplayProps {
  errors: ClpError[];
}

const pluralize = (count: number) => ({
  are: count > 1 ? 'are' : 'is',
  s: count > 1 ? 's' : '',
  these: count > 1 ? 'these' : 'this',
  a: count > 1 ? '' : 'a',
});

const getSystemSubtypeMessage = (errors: ClpError[], p: ReturnType<typeof pluralize>) => {
  if (errors.every(error => error.subtype === 'check')) {
    return `We can't check your file because of a technical issue.`;
  }
  if (errors.every(error => error.subtype === 'history')) {
    return `We can't load your file history because of a technical issue.`;
  }
  return `The system has run into ${p.a ? `${p.a} ` : ''}technical issue${p.s}.`;
};

const getAlertTitle = (errors: ClpError[]) => {
  const p = pluralize(errors.length);
  if (errors.every(error => error.type === 'system')) {
    const subTypeMsg = getSystemSubtypeMessage(errors, p);
    return `System error(s): ${subTypeMsg} Please try again, or, if the problem continues, contact your client support manager.`;
  }
  if (errors.every(error => error.type === 'file')) {
    return `There ${p.are} ${errors.length} issue${p.s} with your file. Before you try again, correct ${p.these} issue${p.s}:`;
  }
  if (errors.every(error => error.type === 'ui')) {
    return `There ${p.are} ${errors.length} issue${p.s}. Before you try again, correct ${p.these} issue${p.s}:`;
  }
  return `There ${p.are} ${errors.length} issue${p.s}. Please review them.`;
};

const ErrorDisplay: FC<ErrorDisplayProps> = ({ errors }) => {
  if (errors.length === 0) {
    return(<div></div>);
  }

  return (
    <div className={styles.error}>
      <Alert
        variant="error"
        size="small"
        accessibleText="Error"
        headingLevel={0}
        icon={{ error: <IconAlertFilled color="error" size="small" aria-hidden data-testid="error-alert-icon" /> }}
        text={<Text primitive="div" textStyle="bodyMediumHeavier" alignment="left" color="loss" data-testid="mds-alert">{getAlertTitle(errors)}</Text>}
      />
      <div className={styles.errorList}>
        {errors.map((error, index) => (
          <Text key={index} primitive="div" textStyle="bodyMedium" alignment="left" color="loss">
            <strong data-testid="error-center-dot" className={styles.centerDot}>{error.message.length >= 1 ? '.' : ''}</strong>{error.message}
          </Text>
        ))}
      </div>
    </div>
  );
};

export default ErrorDisplay;
